/**
 * CL-NEG-01 Task 10 — the buyer's offer entry (listing mode and cart/bundle mode).
 *
 * Two data-loading paths feed one submit path:
 *   - listing mode: `showListingThunk` fetches the single listing being offered on.
 *   - cart mode: `fetchOfferListingsThunk` fetches every listing already in the seller's
 *     basket. The basket itself lives in browser storage (cart.duck), never here.
 *
 * `submitOfferThunk` is the one thing both modes end at: POST /api/offer-initiate, which
 * re-derives price and eligibility from the server's own copy of the listings (SL8) — nothing
 * about money is trusted from this duck beyond the offer figure itself.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { offerInitiate } from '../../util/api';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { lockCartForOffer, selectCart } from '../../ducks/cart.duck';
import { getSellerGroup } from '../../util/custom/cart';

const { UUID } = sdkTypes;

// Every code server/api/custom/offer-initiate.js can answer with, mapped to the key the page
// renders. Written out rather than defaulted, because three of them name a listing and the
// buyer's next action differs for every row (raise the offer / remove an item / try again).
export const OFFER_ERROR_KEYS = {
  'offer-below-minimum': 'BuyerOfferPage.error.offerBelowMinimum',
  'offers-not-enabled': 'BuyerOfferPage.error.offersNotEnabled',
  'stock-unavailable': 'BuyerOfferPage.error.stockUnavailable',
  // Review fix round 1: the same event as stock-unavailable from the buyer's point of view — a
  // piece sold — just caught at a different moment (validateCartListings' pre-hold check,
  // rather than the compare-and-set race stock-unavailable reports). Same wording family.
  'sold-out': 'BuyerOfferPage.error.stockUnavailable',
  'shipping-unpriceable': 'BuyerOfferPage.error.shippingUnpriceable',
  'shipping-not-offered': 'BuyerOfferPage.error.shippingNotOffered',
  'mixed-currency': 'BuyerOfferPage.error.mixedCurrency',
  'total-below-payment-minimum': 'BuyerOfferPage.error.totalTooLow',
  'commission-minimum-unmet': 'BuyerOfferPage.error.commissionMinimum',
  'invalid-offer-amount': 'BuyerOfferPage.error.invalidAmount',
  'empty-cart': 'BuyerOfferPage.error.emptyCart',
  'integration-api-not-configured': 'BuyerOfferPage.error.integrationApiNotConfigured',
};

const firstError = e => e?.data?.errors?.[0] || null;

// ================ Async Thunks ================ //

////////////////////
// Submit an offer //
////////////////////
const submitOfferPayloadCreator = (
  {
    cartItems,
    offerInSubunits,
    message,
    destinationCountry,
    authorId,
    // Review fix round 1: belt for any error code the server has not (yet) been taught to name
    // a listing for. Built by the container from the SAME items it used to build `cartItems` —
    // the buyer's own already-loaded rows — so no extra fetch or Redux-state reach-in is needed
    // here. offer-initiate.js now sends `title` itself for every per-listing code (also fixed
    // this round), so this is a fallback, not the primary path.
    titlesByListingId = {},
  },
  { dispatch, rejectWithValue, extra: sdk }
) =>
  offerInitiate({ cartItems, offerInSubunits, destinationCountry })
    .then(response => {
      const tx = response?.data?.data;
      // The ONLY reliable signal: the replay envelope is otherwise byte-identical to a fresh
      // one (offer-initiate.js).
      const replayed = response?.data?.meta?.replayed === true;
      dispatch(addMarketplaceEntities(response));

      // The lock is a fact about a server-side transaction, so it is written whichever path
      // produced the transaction — a replay's negotiation is priced from the same frozen
      // snapshot as a fresh one.
      if (authorId && tx?.id?.uuid) {
        dispatch(lockCartForOffer({ authorId, transactionId: tx.id.uuid }));
      }

      // D7: the message rides the ordinary, unprivileged messaging endpoint once the
      // transaction exists — offer-initiate deliberately ignores the body field. Not sent on a
      // replay: it would attach today's words to an offer the buyer made days ago, under a
      // figure that was not taken.
      const trimmed = (message || '').trim();
      if (!replayed && trimmed && tx?.id) {
        return sdk.messages
          .send({ transactionId: tx.id, content: trimmed })
          .then(() => ({ transaction: tx, replayed }))
          .catch(e => {
            // The offer exists; only the note failed. Losing the offer over a lost sentence
            // would be the worse outcome, so this is logged and swallowed.
            log.error(e, 'offer-message-send-failed', { transactionId: tx.id.uuid });
            return { transaction: tx, replayed, messageFailed: true };
          });
      }
      return { transaction: tx, replayed };
    })
    .catch(e => {
      const err = firstError(e);
      const listingId = err?.listingId || null;
      return rejectWithValue({
        ...storableError(e),
        code: err?.code || null,
        listingId,
        title: err?.title || (listingId ? titlesByListingId[listingId] || null : null),
      });
    });

export const submitOfferThunk = createAsyncThunk(
  'BuyerOfferPage/submitOffer',
  submitOfferPayloadCreator
);

export const submitOffer = params => dispatch => dispatch(submitOfferThunk(params)).unwrap();

//////////////////
// Show Listing // (listing mode)
//////////////////
const showListingPayloadCreator = (
  { listingId, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  // Never `ownListings`: a buyer offering on their own listing is refused by MakeOfferButton
  // (presentational) and by offer-initiate's own validation (server-side) — this page has no
  // reason to know how to read one.
  return sdk.listings
    .show(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const showListingThunk = createAsyncThunk(
  'BuyerOfferPage/showListing',
  showListingPayloadCreator
);

////////////////////////////
// Fetch Offer Listings   // (cart / bundle mode)
////////////////////////////
const fetchOfferListingsPayloadCreator = (
  { authorId },
  { rejectWithValue, getState, extra: sdk }
) => {
  const group = getSellerGroup(selectCart(getState()), authorId);
  const ids = (group?.items || []).map(i => i.listingId);
  if (ids.length === 0) return Promise.resolve([]);

  return sdk.listings
    .query({
      ids,
      include: ['images', 'author'],
      'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
      ...createImageVariantConfig('listing-card', 400, 0.75),
      ...createImageVariantConfig('listing-card-2x', 800, 0.75),
    })
    .then(response => denormalisedResponseEntities(response))
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchOfferListingsThunk = createAsyncThunk(
  'BuyerOfferPage/fetchOfferListings',
  fetchOfferListingsPayloadCreator
);

// ================ Slice ================ //

const initialState = {
  showListingInProgress: false,
  showListingError: null,
  fetchListingsInProgress: false,
  fetchListingsError: null,
  listings: [],
  submitInProgress: false,
  submitError: null,
  transaction: null,
  replayed: false,
};

const buyerOfferPageSlice = createSlice({
  name: 'BuyerOfferPage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, state => {
        state.showListingInProgress = true;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, state => {
        state.showListingInProgress = false;
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingInProgress = false;
        state.showListingError = action.payload;
      })
      .addCase(fetchOfferListingsThunk.pending, state => {
        state.fetchListingsInProgress = true;
        state.fetchListingsError = null;
      })
      .addCase(fetchOfferListingsThunk.fulfilled, (state, action) => {
        state.fetchListingsInProgress = false;
        state.listings = action.payload;
      })
      .addCase(fetchOfferListingsThunk.rejected, (state, action) => {
        state.fetchListingsInProgress = false;
        state.fetchListingsError = action.payload;
      })
      .addCase(submitOfferThunk.pending, state => {
        state.submitInProgress = true;
        state.submitError = null;
      })
      .addCase(submitOfferThunk.fulfilled, (state, action) => {
        state.submitInProgress = false;
        state.transaction = action.payload.transaction;
        state.replayed = action.payload.replayed;
      })
      .addCase(submitOfferThunk.rejected, (state, action) => {
        state.submitInProgress = false;
        // Task 3 review fix #4: `action.payload` is set only when `submitOfferPayloadCreator`'s
        // own `.catch` runs `rejectWithValue(...)`. A SYNCHRONOUS throw in `util/api.js` (e.g.
        // `serialize(body)` or `window.fetch(...)` itself throwing before a promise is even
        // returned) never reaches that `.catch` — `createAsyncThunk` still dispatches a
        // `rejected` action, but with `payload: undefined` and the raw error serialized onto
        // `action.error` instead. Falling back to a storableError-shaped value built from
        // `action.error` is what keeps `BuyerOfferPage.js`'s `submitError ? ... : null` gate
        // (this same task's other fix) meaningful — without it, this exact shape would render
        // nothing, the symptom just closed one layer up.
        state.submitError = action.payload || storableError(action.error);
      });
  },
});

export const { setInitialState } = buyerOfferPageSlice.actions;
export default buyerOfferPageSlice.reducer;

// ================ Selectors ================ //

export const selectBuyerOfferPage = state => state.BuyerOfferPage;

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  dispatch(setInitialState());
  // Cart mode has no server-side data to load: the basket lives in browser storage and
  // initializeCart has to have run first, so the page fetches its listings in an effect — the
  // same reasoning CartPage.loadData records.
  return params?.id
    ? dispatch(showListingThunk({ listingId: new UUID(params.id), config }))
    : Promise.resolve();
};
